import {
	QueryKey,
	UseQueryOptions,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';

import { useUser } from './useUser';

import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IStore } from '@/interfaces/api/IStore';
import { haggleApiService } from '@/services/haggle.service';

type ICustomQueryOptions = Omit<
	UseQueryOptions<IStore, Error, IStore, QueryKey>,
	'queryKey' | 'queryFn' | 'refetchOnWindowFocus' | 'initialData'
>;

export const useUserStore = (
	options?: ICustomQueryOptions & { withEvents?: boolean },
) => {
	const { user } = useUser();

	const queryClient = useQueryClient();
	const ONE_HOUR = 1000 * 60 * 60;

	const {
		data: store,
		isLoading,
		refetch,
		...rest
	} = useQuery<IStore>({
		queryKey: [ApiQueryKeys.userStore],
		queryFn: () => haggleApiService.getUserStore(options?.withEvents),
		refetchOnWindowFocus: false,
		initialData:
			queryClient.getQueryData<IStore>([ApiQueryKeys.userStore]) ?? undefined,
		staleTime: ONE_HOUR,
		enabled: !!(
			user &&
			!user.roles.includes('admin') &&
			!user.roles.includes('consumer')
		),
		retry: false,
		...options,
	});

	const setStoreInfo = (store: IStore | undefined) => {
		queryClient.setQueryData<IStore | undefined>(
			[ApiQueryKeys.userStore],
			store,
		);
	};

	return {
		store,
		isLoading,
		setStoreInfo,
		refetch,
		...rest,
	};
};
